import { Card, CardContent } from '@shared/ui';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetDriveFilesQuery } from '../../../store/api/apiSlice';
import DriveDetail from './DriveDetail';

const GoogleDriveSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, refetch } = useGetDriveFilesQuery(undefined);
  const allItems = useMemo(
    () => (data?.items || []) as { id: string; name: string; mimeType: string }[],
    [data?.items]
  );
  const folders = useMemo(
    () => allItems.filter((i) => i.mimeType === 'application/vnd.google-apps.folder'),
    [allItems]
  );
  const files = useMemo(
    () => allItems.filter((i) => i.mimeType !== 'application/vnd.google-apps.folder'),
    [allItems]
  );

  const [folderIds, setFolderIds] = useState<string[]>([]);
  const [fileIds, setFileIds] = useState<string[]>([]);
  const [pickerOpen, setPickerOpen] = useState<null | 'folders' | 'files'>(null);
  const [tempSelection, setTempSelection] = useState<string[]>([]);

  useEffect(() => {
    if (allItems.length > 0 && folderIds.length === 0 && fileIds.length === 0) {
      setFolderIds(folders.slice(0, 4).map((f) => f.id));
      setFileIds(files.slice(0, 2).map((f) => f.id));
    }
  }, [allItems.length, fileIds.length, files.slice, folderIds.length, folders.slice]);

  const visibleFolders = useMemo(
    () => folders.filter((f) => folderIds.includes(f.id)),
    [folders, folderIds]
  );
  const visibleFiles = useMemo(() => files.filter((f) => fileIds.includes(f.id)), [files, fileIds]);

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: 2 }}>
        <DriveDetail
          back={() => navigate('/settings/integrations')}
          iconSrc={'https://ssl.gstatic.com/images/branding/product/2x/drive_48dp.png'}
          isLoading={isLoading}
          onRefresh={() => refetch()}
          visibleFolders={visibleFolders}
          visibleFiles={visibleFiles}
          drivePickerOpen={pickerOpen}
          folders={folders}
          files={files}
          tempSelection={tempSelection}
          setTempSelection={setTempSelection}
          openFoldersPicker={() => {
            setTempSelection(folderIds);
            setPickerOpen('folders');
          }}
          openFilesPicker={() => {
            setTempSelection(fileIds);
            setPickerOpen('files');
          }}
          closePicker={() => setPickerOpen(null)}
          applyPicker={() => {
            if (pickerOpen === 'folders') setFolderIds(tempSelection);
            if (pickerOpen === 'files') setFileIds(tempSelection);
            setPickerOpen(null);
          }}
        />
      </CardContent>
    </Card>
  );
};

export default GoogleDriveSettingsPage;
